import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'

/**
 * Law identity + version metadata forwarded to the reading surfaces' citation
 * attributes, so copies and markings record which version they were made on.
 */
export interface LawCitationInfo {
  id?: string
  title?: string | null
  shortTitle?: string | null
  timestamp?: string | null
  dateOfPublication?: string | null
  dateInForce?: string | null
  lastChangeInForce?: string | null
}

/**
 * Transforms a short title string into an object containing the title and abbreviation.
 * @param text The short title string to transform.
 * @returns An object containing the title and abbreviation, or null if the input is invalid.
 */

export function transformShortTitle(text: string | null | undefined): { title: string | null | undefined, abbreviation: string | null | undefined } {
  if (!text) {
    return {
      title: null,
      abbreviation: null,
    }
  }

  // Require spaces around separators so hyphenated law names are not misparsed
  // eslint-disable-next-line regexp/no-super-linear-backtracking
  const match = text.match(/^\s*(.*?)\s+[–-]\s*(?:\(\s*)?([^\s()]+?\.?)\s*(?:\)\s*)?$/)
  if (match) {
    const title = match[1]?.trim()
    const abbreviation = match[2]?.trim()
    return {
      title: title || null,
      abbreviation: abbreviation || null,
    }
  }
  else {
    return {
      title: text.trim(),
      abbreviation: null,
    }
  }
}

function romanToNumber(roman: string): number {
  const values: Record<string, number> = { i: 1, v: 5, x: 10, l: 50, c: 100, d: 500, m: 1000 }
  let result = 0
  const chars = roman.toLowerCase().split('')
  for (let i = 0; i < chars.length; i++) {
    const char = chars[i]
    const nextChar = chars[i + 1]
    const current = char ? values[char] || 0 : 0
    const next = nextChar ? values[nextChar] || 0 : 0
    result += current < next ? -current : current
  }
  return result
}

function compareKeys(a: number[], b: number[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const numA = a[i] ?? 0
    const numB = b[i] ?? 0
    if (numA !== numB) {
      return numA - numB
    }
  }
  return 0
}

/**
 * Parse lawFragmentId into sortable numeric segments
 */
export function parseLawFragmentId(id: string): number[] {
  const segments: number[] = []
  const parts = id.split('/')

  for (const part of parts) {
    const roman = part.match(/underkapittel-([ivxlcdm]+)/i)?.[1]
    if (roman) {
      segments.push(romanToNumber(roman))
    }
    else {
      const numbers = part.match(/\d+/g)
      if (numbers) {
        segments.push(...numbers.map(Number))
      }
    }
  }

  return segments
}

/**
 * Compare two lawFragmentId strings for sorting
 */
export function compareLawFragmentIds(a: string, b: string): number {
  return compareKeys(parseLawFragmentId(a), parseLawFragmentId(b))
}

/**
 * Sort an array of documents by their lawFragmentId field
 */
export function sortByLawFragmentId<T extends { lawFragmentId?: string | null }>(docs: T[]): T[] {
  return docs
    .map(doc => ({ doc, key: parseLawFragmentId(doc.lawFragmentId || '') }))
    .sort((a, b) => compareKeys(a.key, b.key))
    .map(({ doc }) => doc)
}

/**
 * Finds the ezMainNodeId for the top-level legal commentary for a specific law.
 */
export function findTopLevelLawCommentEzId(comments?: LawAndCommentary[] | null, lawId?: string | null) {
  if (!comments?.length || !lawId) {
    return undefined
  }

  const normalizedLawId = lawId.toLowerCase()

  return comments.find((comment) => {
    if (!comment.lawId || comment.lawId.toLowerCase() !== normalizedLawId) {
      return false
    }

    if (comment.nodeType === 'legal_commentary') {
      return true
    }

    if (comment.nodeType === 'legal_commentary_section') {
      return false
    }

    return !comment.lawFragmentId
  })?.ezMainNodeId ?? undefined
}

/**
 * Uppercases the Roman-numeral suffix of any `/`-delimited segment in a law id (e.g.
 * `underkapittel-ii` → `underkapittel-II`, `kapittel-iv/...` → `kapittel-IV/...`). Segments whose
 * trailing `-`-separated part contains digits are left alone, so `paragraf-8-3` and
 * `lov-2005-06-17-90` are unchanged. Used to retry case-sensitive Solr `id` lookups when the
 * frontend has lowercased an href.
 */
export function uppercaseRomanSegments(id: string): string {
  return id.replace(/(^|\/)([^/]+)/g, (match, slash, segment) => {
    const m = segment.match(/^(.*?)-([ivxlcdm]+)$/i)
    return m ? `${slash}${m[1]}-${m[2].toUpperCase()}` : match
  })
}

/**
 * Builds a display number for an NS standard node from the trailing id segment:
 * digits become a dotted number (`.../punkt-46/punkt-46-1` → `46.1`), and a
 * Roman-numeral suffix is uppercased (`.../kapittel-IV` → `IV`). Returns null
 * when the leaf carries neither.
 */
export function getNsItemNumber(id: string | null | undefined): string | null {
  if (!id) {
    return null
  }
  const leaf = id.split('/').pop()
  if (!leaf) {
    return null
  }
  const digits = leaf.match(/\d+/g)
  if (digits?.length) {
    return digits.join('.')
  }
  const roman = leaf.match(/-([ivxlcdm]+)$/i)
  return roman ? roman[1]!.toUpperCase() : null
}

function normalizeBodytext(html: string | null | undefined): string {
  return (html ?? '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim().toLowerCase()
}

// Only treat text as duplicate when the matching part is long enough. This avoids false matches on short phrases
const DUPLICATE_OVERLAP_THRESHOLD = 30

/**
 * Render section body text only when it is not already covered by `ledd-*` children.
 *
 * If the section and child contain the same text, hide the section copy.
 * Otherwise, keep it as real intro text.
 */
export function shouldShowOwnBodytext(node: Pick<LawAndCommentary, 'nodeType' | 'bodytext' | 'children'>, isStandardText: boolean): boolean {
  if (!node.bodytext) {
    return false
  }
  if (isStandardText) {
    return node.nodeType !== 'note'
  }
  if (node.nodeType === 'subsection' || node.nodeType === 'note') {
    return false
  }
  if (node.nodeType === 'section') {
    const own = normalizeBodytext(node.bodytext)
    if (!own) {
      return false
    }
    const subsectionChildren = node.children?.filter(child => child.nodeType === 'subsection') ?? []
    return !subsectionChildren.some((child) => {
      const childText = normalizeBodytext(child.bodytext)
      const overlap = own.includes(childText) ? childText.length : childText.includes(own) ? own.length : 0
      return overlap >= DUPLICATE_OVERLAP_THRESHOLD
    })
  }
  return true
}

/**
 * True when a section shows its own body text as the first ledd.
 * Used by both the TOC and reading panel, so they stay in sync.
 */
export function hasLeadingBodytextLedd(node: Pick<LawAndCommentary, 'nodeType' | 'bodytext' | 'children'>, isStandardText: boolean): boolean {
  if (isStandardText || !shouldShowOwnBodytext(node, isStandardText)) {
    return false
  }
  return node.children?.some(child => child.nodeType === 'subsection') ?? false
}

/**
 * Group paragraph child results under their parent paragraph card, with fallbacks for notes and footnotes
 */
export function collapseLawFragmentChildId(rawId: string): string {
  return rawId
    .toLowerCase()
    .replace(/(\/paragraf-[^/]+)\/.*$/, '$1')
    .replace(/\/(?:ledd|note|footnote)-.*$/, '')
}

/**
 * Lowercases the first letter of a law title for use mid-sentence, e.g. "Kommentar til personvernforordningen (GDPR)".
 * Titles that start with an acronym (EØS-konkurranseloven, CSRD) or contain title-cased words
 * (Sustainable Finance Disclosure Regulation) are names and are returned unchanged.
 */
export function lawTitleInRunningText(title: string | null | undefined): string | null {
  const trimmed = title?.trim()
  if (!trimmed) {
    return null
  }
  const [firstWord = '', ...otherWords] = trimmed.split(/\s+/)
  const startsWithAcronym = /^\p{L}\p{Lu}/u.test(firstWord)
  const hasTitleCasedWord = otherWords.some(word => /^\p{Lu}\p{Ll}/u.test(word))
  if (startsWithAcronym || hasTitleCasedWord) {
    return trimmed
  }
  return trimmed.charAt(0).toLocaleLowerCase('nb-NO') + trimmed.slice(1)
}

/**
 *  Extracts a reference from a commentary title, such as "§ 12", "Kapittel 5", "§ 3-2", etc.
 */
export function extractReference(title: string | null | undefined): string | null {
  if (!title) {
    return null
  }
  const match = title.match(/^(§\s*\d+(?:[a-z]|\s+[A-Za-z]\b)?(?:-\d+(?:[a-z]|\s+[A-Za-z]\b)?)*|Kapittel\s+[\dIVXLCDMivxlcdm]+(?:\s*[A-Za-z]\b)?)/)
  return match?.[1] ?? null
}
